import React, { useMemo, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDrop } from 'react-dnd';
import { format, differenceInCalendarDays, parseISO, addDays as addDaysFn } from 'date-fns';
import { updatePlan, savePlan, actions, fetchPlanner, fetchProcessCompletionStatus, fetchByWorkTicketIdRaw } from '../../store/slice';
import { selectPrograms, selectPlanFilters } from '../../store/selectors';
import { DRAG_TYPES } from './constants';
import ProgramBar from './ProgramBar';
import { splitProgramByYears, stackPrograms } from './programUtils';
import { showModal } from 'Core/redux/main/slice';
import { MODAL_TYPES } from 'Feature/components/app-modals';

const { PROGRAM_ADD_EDIT } = MODAL_TYPES;

const isNilOrEmpty = v => v == null || (typeof v === 'string' && v.trim() === '');

const normalizeProcessDatesForDrop = (process, dropSinceIsoLike, existingToDate) => {
  const next = { ...(process || {}) };
  // For scheduling we use fromDate/toDate fields (modal/edit flow). Keep since/till as fallbacks.
  next.fromDate = dropSinceIsoLike;
  next.since = dropSinceIsoLike;
  if (!isNilOrEmpty(existingToDate)) {
    next.toDate = existingToDate;
    next.till = existingToDate;
  }
  return next;
};

const resolveToDate = data => {
  return (
    data?.toDate ||
    data?.ToDate ||
    data?.to_date ||
    data?.todate ||
    data?.till ||
    data?.Till ||
    data?.EndDate ||
    data?.endDate ||
    ''
  );
};

/** Match minimal saveplan-style scheduling (see box-with-handle): machine, window, work-ticket id — not processParts. */
const ensureTillForSavePlanIfMissing = (process, dropSinceIsoLike) => {
  const next = { ...(process || {}) };
  if (!isNilOrEmpty(resolveToDate(next))) return next;
  const since = next.since || next.fromDate || dropSinceIsoLike;
  if (isNilOrEmpty(since)) return next;
  const datePart = String(since).split('T')[0];
  if (isNilOrEmpty(datePart)) return next;
  // End of same calendar day with explicit time (avoids T24 and matches API DateTime/string expectations).
  const endOfDay = `${datePart}T23:59:59`;
  next.till = endOfDay;
  next.toDate = endOfDay;
  return next;
};

/** Day-mode columns: use wall-clock now on the chosen calendar day (not midnight). Hourly headers keep `iso`. */
function combineCalendarDateWithLocalTime(calendarDate) {
  const clock = new Date();
  return new Date(
    calendarDate.getFullYear(),
    calendarDate.getMonth(),
    calendarDate.getDate(),
    clock.getHours(),
    clock.getMinutes(),
    clock.getSeconds(),
    0,
  );
}

/** Calendar for a column: prefer header cell (authoritative for rendered columns) over days[i] when lengths differ. */
const localDateFromTimelineColumn = (headerCell, daysCell) => {
  if (!headerCell) return null;
  if (headerCell.hour !== undefined && headerCell.iso) {
    try {
      return parseISO(String(headerCell.iso).replace(' ', 'T'));
    } catch {
      return null;
    }
  }
  if (headerCell.iso && typeof headerCell.iso === 'string') {
    const datePart = headerCell.iso.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [y, m, d] = datePart.split('-').map(Number);
      return new Date(y, m - 1, d, 0, 0, 0, 0);
    }
  }
  if (daysCell?.date instanceof Date) return new Date(daysCell.date.getTime());
  if (daysCell?.date) return new Date(daysCell.date);
  return null;
};

/**
 * Task-board → calendar: resolve column index from header cell geometry in the SAME grid as the drop row.
 * document.querySelector would pick the wrong header when normal + fullscreen grids both exist in the DOM.
 */
function plannerGridRootFromDropZone(dropZoneEl) {
  if (typeof document === 'undefined' || !dropZoneEl?.closest) return null;
  return dropZoneEl.closest('[data-planner-v2-grid]');
}

function taskBoardDayIndexFromHeaderClientX(clientX, dropZoneEl) {
  if (typeof document === 'undefined') return null;
  const root = plannerGridRootFromDropZone(dropZoneEl);
  const band = root?.querySelector?.('[data-timeline-header]');
  if (!band) return null;
  const cells = band.querySelectorAll('[data-planner-day-index]');
  if (!cells || !cells.length) return null;

  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i];
    const r = cell.getBoundingClientRect();
    if (clientX >= r.left && clientX < r.right) {
      const idx = parseInt(cell.getAttribute('data-planner-day-index'), 10);
      if (!Number.isNaN(idx)) return idx;
    }
  }

  let bestIdx = null;
  let bestDist = Infinity;
  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i];
    const r = cell.getBoundingClientRect();
    const mid = (r.left + r.right) / 2;
    const d = Math.abs(clientX - mid);
    if (d < bestDist) {
      bestDist = d;
      const idx = parseInt(cell.getAttribute('data-planner-day-index'), 10);
      bestIdx = Number.isNaN(idx) ? null : idx;
    }
  }
  return bestIdx;
}

/** Snap task-board preview to the same pixel box as the date header cell in this grid instance. */
function getTaskBoardPreviewLayoutInDropZone(dayIndex, dropZoneEl) {
  if (typeof document === 'undefined' || dropZoneEl == null || dayIndex == null) return null;
  const root = plannerGridRootFromDropZone(dropZoneEl);
  if (!root) return null;
  const cell = root.querySelector(`[data-timeline-header] [data-planner-day-index="${dayIndex}"]`);
  if (!cell) return null;
  const cr = cell.getBoundingClientRect();
  const dr = dropZoneEl.getBoundingClientRect();
  const left = cr.left - dr.left;
  const width = cr.width;
  if (width <= 0 || !Number.isFinite(left)) return null;
  return { left, width };
}

const hasRequiredSavePlanDropFields = data => {
  const ch = data?.channelUuid ?? data?.machineId;
  if (isNilOrEmpty(ch)) return false;
  const since = data?.since ?? data?.fromDate ?? '';
  if (isNilOrEmpty(since)) return false;
  const till = resolveToDate(data);
  if (isNilOrEmpty(till)) return false;
  const identity =
    data?.id ??
    data?.title ??
    data?.workTicketNumber ??
    data?.workTicketNo ??
    data?.workTicket ??
    data?.WorkTicketNumber;
  if (identity == null) return false;
  if (typeof identity === 'string' && identity.trim() === '') return false;
  return true;
};

function ChannelRow({
  channel,
  programs,
  days,
  headerData,
  timelineWidth,
  programCellWidth,
  headerCellWidth,
  onProgramDrop,
  onProgramEdit,
  sidebarWidth = 120,
  onDragPreviewChange,
  searchMatchedProgramIds = null,
  leftGutterWidth = 0,
  rightGutterWidth = 0,
  timelineContentWidth = null,
}) {
  const dispatch = useDispatch();
  const allProgramsFromStore = useSelector(selectPrograms);
  const planFilters = useSelector(selectPlanFilters);
  const [dragPreview, setDragPreview] = useState(null);
  /** Measure task-board drops against this node so X lines up with row cells (header querySelector can be offset). */
  const timelineDropZoneRef = useRef(null);

  const viewMode = headerData[0]?.viewMode || 'day';
  const effectiveCellWidth = (viewMode === 'year-multi' || viewMode === 'month')
    ? headerCellWidth
    : programCellWidth;

  const dayIndexFromClientX = useCallback(
    (clientX) => {
      const el = timelineDropZoneRef.current;
      if (!el || effectiveCellWidth <= 0) return null;
      const rect = el.getBoundingClientRect();
      const relativeX = clientX - rect.left;
      const idx = Math.floor(relativeX / effectiveCellWidth);
      return Math.max(0, Math.min(headerData.length - 1, idx));
    },
    [effectiveCellWidth, headerData],
  );

  const taskBoardDayIndex = useCallback(
    (clientX, dropZoneEl) => {
      const fromHeader = taskBoardDayIndexFromHeaderClientX(clientX, dropZoneEl);
      if (fromHeader != null) {
        return Math.max(0, Math.min(headerData.length - 1, fromHeader));
      }
      return dayIndexFromClientX(clientX);
    },
    [dayIndexFromClientX, headerData.length],
  );

  const [{ isOver }, drop] = useDrop(() => ({
    accept: [DRAG_TYPES.PROGRAM, DRAG_TYPES.RESIZE_LEFT, DRAG_TYPES.RESIZE_RIGHT, DRAG_TYPES.DRAGGABLE_ITEM],
    hover: (item, monitor) => {
      if (item.isDraggableItem) {
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;
        const grab =
          typeof item.sourceGrabOffsetX === 'number' && Number.isFinite(item.sourceGrabOffsetX)
            ? item.sourceGrabOffsetX
            : 0;
        const anchorX = clientOffset.x - grab;
        const cellWidth = effectiveCellWidth;
        const dayIndex = taskBoardDayIndex(anchorX, timelineDropZoneRef.current);
        if (dayIndex == null) return;
        const headerCol = headerData[dayIndex];
        const targetDate = localDateFromTimelineColumn(headerCol, days[dayIndex]);
        if (headerCol && targetDate && !Number.isNaN(targetDate.getTime())) {
          const previewDate = new Date(targetDate);
          const previewEndDate = new Date(targetDate.getTime() + 24 * 60 * 60 * 1000);
          const layout = getTaskBoardPreviewLayoutInDropZone(dayIndex, timelineDropZoneRef.current);
          const left = layout != null ? layout.left : dayIndex * cellWidth;
          const width = layout != null ? layout.width : Math.max(cellWidth - 8, 32);
          setDragPreview({
            id: item.id,
            since: previewDate.toISOString(),
            till: previewEndDate.toISOString(),
            sinceLabel: format(previewDate, 'dd MMM yyyy'),
            tillLabel: format(previewEndDate, 'dd MMM yyyy'),
            title: item.title || 'New Task',
            isDraggableItem: true,
            left,
            width,
          });
        }
        return;
      }

      const delta = monitor.getDifferenceFromInitialOffset();
      if (!delta) return;
      const cellWidth = effectiveCellWidth;
      const rawShiftDays = delta.x / cellWidth;
      const shiftDays = Math.round(rawShiftDays);
      const currentProgram = allProgramsFromStore.find(p => p.id === item.id);
      const currentSince = currentProgram ? currentProgram.since : item.since;
      const currentTill = currentProgram ? currentProgram.till : item.till;
      if (!currentSince || !currentTill) return;

      let previewSince, previewTill;
      if (item.resizeType === 'left') {
        const originalSince = parseISO(currentSince);
        const originalTill = parseISO(currentTill);
        previewSince = addDaysFn(originalSince, shiftDays);
        previewTill = originalTill;
        if (previewSince >= previewTill) previewSince = addDaysFn(previewTill, -1);
      } else if (item.resizeType === 'right') {
        const originalSince = parseISO(currentSince);
        const originalTill = parseISO(currentTill);
        previewSince = originalSince;
        previewTill = addDaysFn(originalTill, shiftDays);
        if (previewTill <= previewSince) previewTill = addDaysFn(previewSince, 1);
      } else {
        previewSince = addDaysFn(parseISO(currentSince), shiftDays);
        previewTill = addDaysFn(parseISO(currentTill), shiftDays);
      }

      const timelineStartIso = String(headerData[0]?.iso || days[0]?.iso || '').slice(0, 10);
      const leftDays =
        previewSince && /^\d{4}-\d{2}-\d{2}$/.test(timelineStartIso)
          ? differenceInCalendarDays(previewSince, parseISO(timelineStartIso))
          : 0;
      const durationDays = Math.max(1, differenceInCalendarDays(previewTill, previewSince) + 1);
      const targetDayIndex = Math.max(0, Math.min(headerData.length - 1, leftDays));
      const preview = {
        left: leftDays * effectiveCellWidth,
        width: durationDays * effectiveCellWidth - 8,
        sinceLabel: format(previewSince, 'dd MMM yyyy'),
        tillLabel: format(previewTill, 'dd MMM yyyy'),
        resizeType: item.resizeType,
        shiftDays: shiftDays,
        targetDay: targetDayIndex,
      };
      setDragPreview(preview);
      if (onDragPreviewChange) onDragPreviewChange(preview);
    },
    drop: (item, monitor) => {
      try {
        if (item.isDraggableItem) {
          const clientOffset = monitor.getClientOffset();
          if (!clientOffset) return;
          const grab =
            typeof item.sourceGrabOffsetX === 'number' && Number.isFinite(item.sourceGrabOffsetX)
              ? item.sourceGrabOffsetX
              : 0;
          const anchorX = clientOffset.x - grab;
          const dayIndex = taskBoardDayIndex(anchorX, timelineDropZoneRef.current);
          if (dayIndex == null) return;
          const cellWidth = effectiveCellWidth;
          const headerCol = headerData[dayIndex];
          const targetDate = localDateFromTimelineColumn(headerCol, days[dayIndex]);
          if (!headerCol || !targetDate || Number.isNaN(targetDate.getTime())) return;

          const dropSince =
            headerCol.hour !== undefined && headerCol.iso
              ? String(headerCol.iso).replace(' ', 'T')
              : format(combineCalendarDateWithLocalTime(targetDate), "yyyy-MM-dd'T'HH:mm:ss");
          const newChannelUuid = channel.uuid || channel.id || channel.assetNumber || channel.title;

          // Validate by fetching the full work ticket details (getById)
          dispatch(fetchByWorkTicketIdRaw(item.id))
              .unwrap()
              .then(payload => {
                const apiData = payload?.response || payload?.data?.response || payload?.data || payload || {};
                const apiToDate = resolveToDate(apiData);

                const ticket = String(
                  apiData.workTicketNumber ??
                    apiData.workTicketNo ??
                    apiData.workTicket ??
                    apiData.title ??
                    item.title ??
                    item.id ??
                    '',
                ).trim();
                const cleanTicket = ticket.replace(/^W/i, '');

                const rawPid = apiData.id;
                const hasRealProcessId =
                  rawPid != null &&
                  rawPid !== '' &&
                  !(typeof rawPid === 'number' && (rawPid === 0 || Number.isNaN(rawPid))) &&
                  String(rawPid).trim() !== '' &&
                  String(rawPid).trim() !== '0';

                const merged = {
                  ...apiData,
                  machineId: newChannelUuid,
                  channelUuid: newChannelUuid,
                  // Match Assign + savePlan thunk: Title = work ticket for SP; Id = W + ticket when no process row yet
                  title: cleanTicket || ticket,
                  id: hasRealProcessId ? String(rawPid).trim() : `W${cleanTicket || ticket}`,
                };

                let processForDrop = normalizeProcessDatesForDrop(merged, dropSince, apiToDate);
                processForDrop = ensureTillForSavePlanIfMissing(processForDrop, dropSince);

                if (!hasRequiredSavePlanDropFields(processForDrop)) {
                  // Open existing Plan modal so user can fill missing values before placement
                  dispatch(
                    showModal(PROGRAM_ADD_EDIT, {
                      ...processForDrop,
                      __fromTaskBoardDrop: true,
                      __removeTaskBoardElementId: String(item.id),
                    }),
                  );
                  return;
                }

                // Fields are valid -> saveplan (new placement from Task board), not updateprocess (modal Save only)
                const tillForSave = resolveToDate(processForDrop);
                const sinceForSave = processForDrop.since ?? processForDrop.fromDate ?? '';
                const savePlanPayload = {
                  title: String(processForDrop.title || cleanTicket || ticket).replace(/^W+/i, ''),
                  channelUuid: processForDrop.channelUuid ?? processForDrop.machineId ?? newChannelUuid,
                  machineId: processForDrop.machineId ?? processForDrop.channelUuid ?? newChannelUuid,
                  description: processForDrop.description ?? '',
                  since: String(sinceForSave).replace(' ', 'T'),
                  till: String(tillForSave).replace(' ', 'T'),
                };
                dispatch(savePlan(savePlanPayload))
                  .unwrap()
                  .then(() => {
                    dispatch(actions.removeElement(String(item.id)));
                    const filters = planFilters || {};
                    dispatch(
                      fetchPlanner({
                        searchText: filters.searchText,
                        status: filters.status,
                        start: filters.start,
                        end: filters.end,
                      }),
                    );
                    dispatch(fetchProcessCompletionStatus());
                  })
                  .catch(() => {});
              })
              .catch(() => {});
          return;
        }

        const delta = monitor.getDifferenceFromInitialOffset();
        if (!delta) return;
        if (!item.id || !item.channelUuid || !item.since || !item.till) return;
        const cellWidth = effectiveCellWidth;
        const shiftDays = Math.round(delta.x / cellWidth);
        const currentProgram = allProgramsFromStore.find(p => p.id === item.id);
        const currentSince = currentProgram ? currentProgram.since : item.since;
        const currentTill = currentProgram ? currentProgram.till : item.till;
        if (!currentSince || !currentTill) return;

        const newChannelUuid = channel.uuid || channel.id || channel.assetNumber || channel.title;
        const machineChanged = item.channelUuid !== newChannelUuid;
        if (Math.abs(shiftDays) === 0 && !machineChanged) return;

        let since, till;
        if (item.resizeType === 'left') {
          const originalSince = parseISO(currentSince);
          const originalTill = parseISO(currentTill);
          since = addDaysFn(originalSince, shiftDays);
          till = originalTill;
          if (since >= till) since = addDaysFn(till, -1);
        } else if (item.resizeType === 'right') {
          const originalSince = parseISO(currentSince);
          const originalTill = parseISO(currentTill);
          since = originalSince;
          till = addDaysFn(originalTill, shiftDays);
          if (till <= since) till = addDaysFn(since, 1);
        } else {
          since = addDaysFn(parseISO(currentSince), shiftDays);
          till = addDaysFn(parseISO(currentTill), shiftDays);
        }

        const originalProgram = allProgramsFromStore.find(p => p.id === item.id);
        if (!originalProgram) return;

        const title = originalProgram.title || originalProgram.workTicketNumber || originalProgram.workTicketNo || 'Untitled';
        const formattedSince = format(since, "yyyy-MM-dd'T'HH:mm:ss");
        const formattedTill = format(till, "yyyy-MM-dd'T'HH:mm:ss");
        const payload = {
          ...originalProgram,
          id: item.id,
          channelUuid: newChannelUuid,
          machineId: newChannelUuid,
          since: formattedSince,
          till: formattedTill,
          title: title,
          description: originalProgram.description || '',
          userId: localStorage.getItem('UserId') || originalProgram.userId,
        };
        if (onProgramDrop) onProgramDrop(payload);
        dispatch(updatePlan(payload))
          .unwrap()
          .then(() => {
            // Immediately refresh planner data and completion status so UI reflects the move
            const filters = planFilters || {};
            dispatch(
              fetchPlanner({
                searchText: filters.searchText,
                status: filters.status,
                start: filters.start,
                end: filters.end,
              }),
            );
            dispatch(fetchProcessCompletionStatus());
          })
          .catch(() => {});
      } finally {
        setDragPreview(null);
        if (onDragPreviewChange) onDragPreviewChange(null);
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  }), [allProgramsFromStore, headerData, timelineWidth, days, onProgramDrop, onDragPreviewChange, channel, programCellWidth, headerCellWidth, effectiveCellWidth, planFilters, dispatch, taskBoardDayIndex]);

  const setTimelineDropRef = useCallback(
    (node) => {
      timelineDropZoneRef.current = node;
      drop(node);
    },
    [drop],
  );

  const { items } = useMemo(() => {
    // Deduplicate programs by their id/work-ticket identifiers to avoid rendering duplicates per channel
    const uniqueMap = new Map();
    programs.forEach(p => {
      const key =
        p?.id ||
        p?.originalId ||
        p?.workTicketNumber ||
        p?.workTicketNo ||
        p?.workTicket ||
        p?.processIdExt;
      if (key) {
        if (!uniqueMap.has(key)) {
          uniqueMap.set(key, p);
        }
      } else {
        // fallback: use reference
        uniqueMap.set(p, p);
      }
    });
    const uniquePrograms = Array.from(uniqueMap.values());

    const sortedPrograms = uniquePrograms.sort((a, b) => {
      const aSince = new Date(a.since);
      const aTill = new Date(a.till);
      const bSince = new Date(b.since);
      const bTill = new Date(b.till);
      const aYearsSpanned = aTill.getFullYear() - aSince.getFullYear() + 1;
      const bYearsSpanned = bTill.getFullYear() - bSince.getFullYear() + 1;
      if (aYearsSpanned !== bYearsSpanned) return aYearsSpanned - bYearsSpanned;
      return aSince - bSince;
    });
    const splitPrograms = [];
    sortedPrograms.forEach(p => {
      const segments = splitProgramByYears(p, headerData, viewMode);
      splitPrograms.push(...segments);
    });
    return stackPrograms(splitPrograms, headerData);
  }, [programs, headerData, viewMode]);

  const cellWidth = effectiveCellWidth;

  const timelineBg = isOver ? 'rgba(25, 118, 210, 0.08)' : 'transparent';
  const gutterBg = timelineBg;

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid #e0e0e0' }}>
      <div
        style={{
          width: sidebarWidth,
          padding: '8px 8px',
          background: isOver ? '#e3f2fd' : '#f9fbff',
          borderRight: '1px solid #e0e0e0',
          textAlign: 'right',
          transition: 'background-color 0.2s ease',
        }}
      >
        <div style={{ fontWeight: isOver ? 700 : 600, color: isOver ? '#1565c0' : '#1976d2', transition: 'all 0.2s ease' }}>
          {channel.title || channel.assetNumber || channel.uuid}
        </div>
      </div>
      {leftGutterWidth > 0 && (
        <div
          style={{
            width: leftGutterWidth,
            flexShrink: 0,
            minHeight: 56,
            background: gutterBg,
            transition: 'background-color 0.2s ease',
          }}
          aria-hidden="true"
        />
      )}
      <div
        ref={setTimelineDropRef}
        style={{
          position: 'relative',
          overflowX: 'hidden',
          overflowY: 'visible',
          width: timelineContentWidth ?? undefined,
          flex: timelineContentWidth ? 'none' : 1,
          minWidth: timelineContentWidth ?? undefined,
          minHeight: 56,
          background: timelineBg,
          transition: 'background-color 0.2s ease',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {headerData.map((d, i) => (
            <div
              key={d.iso || d.value}
              style={{
                position: 'absolute',
                left: i * effectiveCellWidth,
                width: effectiveCellWidth,
                top: 0,
                bottom: 0,
                borderRight: '1px solid #e0e0e0',
              }}
            />
          ))}
        </div>

        {(() => {
          const segmentGroups = new Map();
          items.forEach(p => {
            if (p.isSegment && p.originalId) {
              if (!segmentGroups.has(p.originalId)) {
                segmentGroups.set(p.originalId, []);
              }
              segmentGroups.get(p.originalId).push(p);
            }
          });
          const segmentWidths = new Map();
          segmentGroups.forEach((segments, originalId) => {
            if (segments.length > 1) {
              const sortedSegments = [...segments].sort((a, b) => new Date(a.since) - new Date(b.since));
              const firstSegment = sortedSegments[0];
              const lastSegment = sortedSegments[sortedSegments.length - 1];
              const startIsoForCalc = (headerData[0]?.viewMode === 'year-multi' || headerData[0]?.viewMode === 'month')
                ? (headerData[0]?.iso ? headerData[0].iso + 'T00:00:00' : new Date().toISOString())
                : (headerData[0]?.viewMode === 'day' || headerData[0]?.viewMode === 'hour') && headerData[0]?.iso
                  ? (headerData[0].viewMode === 'hour'
                      ? String(headerData[0].iso).replace(' ', 'T')
                      : `${String(headerData[0].iso).slice(0, 10)}T00:00:00`)
                  : (days[0]?.iso ? days[0].iso + 'T00:00:00' : new Date().toISOString());
              const startDate = parseISO(startIsoForCalc);
              const firstStart = parseISO(firstSegment.since);
              const lastEnd = parseISO(lastSegment.till);
              const firstLeftDays = differenceInCalendarDays(firstStart, startDate);
              const lastRightDays = differenceInCalendarDays(lastEnd, startDate) + 1;
              const totalWidth = (lastRightDays - firstLeftDays) * cellWidth;
              segmentWidths.set(originalId, totalWidth);
            }
          });

            return items.map(p => {
            if (!p.id || !p.since || !p.till) return null;
            const startIsoForBar = (headerData[0]?.viewMode === 'year-multi' || headerData[0]?.viewMode === 'month')
              ? (headerData[0]?.iso ? headerData[0].iso + 'T00:00:00' : new Date().toISOString())
              : (headerData[0]?.viewMode === 'day' || headerData[0]?.viewMode === 'hour') && headerData[0]?.iso
                ? (headerData[0].viewMode === 'hour'
                    ? String(headerData[0].iso).replace(' ', 'T')
                    : `${String(headerData[0].iso).slice(0, 10)}T00:00:00`)
                : (days[0]?.iso ? days[0].iso + 'T00:00:00' : new Date().toISOString());
            const totalSegmentWidth = (p.isSegment && p.isFirstSegment && p.originalId && segmentWidths.has(p.originalId))
              ? segmentWidths.get(p.originalId)
              : null;
            const programId = p.originalId ?? p.id;
            const isSearchMatch =
              searchMatchedProgramIds && programId != null && searchMatchedProgramIds.has(String(programId));
            return (
              <div key={p.id} style={{ position: 'absolute', top: 6 }}>
                <ProgramBar
                  program={p}
                  cellWidth={cellWidth}
                  startIso={startIsoForBar}
                  onDoubleClick={() => {
                    // Log the full planner program object when user opens the modal
                    console.log('[PlannerV2] Program bar double-clicked:', {
                      id: p.id,
                      originalId: p.originalId,
                      since: p.since,
                      till: p.till,
                      rawProgram: p,
                    });
                    onProgramEdit?.(p.originalId || p.id);
                  }}
                  viewMode={headerData[0]?.viewMode || 'day'}
                  headerData={headerData}
                  horizontalIdx={p.horizontalIdx || 0}
                  overlapCount={p.overlapCount || 1}
                  totalSegmentWidth={totalSegmentWidth}
                  isSearchMatch={isSearchMatch}
                />
              </div>
            );
          });
        })()}

        {dragPreview && isOver && (
          <>
            <div
              style={{
                position: 'absolute',
                left: dragPreview.left,
                width: dragPreview.width,
                height: 46,
                top: 6,
                background: 'rgba(25, 118, 210, 0.3)',
                border: '2px dashed #1976d2',
                borderRadius: 8,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '6px 10px',
                zIndex: 20,
                pointerEvents: 'none',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
              }}
            >
              <div style={{ fontWeight: 600, color: '#1976d2', fontSize: 13, lineHeight: 1.1, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                {dragPreview.resizeType === 'left' ? '← Resize Start' : dragPreview.resizeType === 'right' ? 'Resize End →' : 'Moving...'}
              </div>
              <div style={{ color: '#1976d2', fontSize: 12 }}>
                {dragPreview.sinceLabel} - {dragPreview.tillLabel}
                {Number.isFinite(dragPreview.shiftDays) && (
                  <span style={{ color: '#666', fontSize: 10, marginLeft: 4 }}>
                    ({dragPreview.shiftDays > 0 ? '+' : ''}
                    {dragPreview.shiftDays} days)
                  </span>
                )}
              </div>
            </div>

            <div
              style={{
                position: 'absolute',
                left: dragPreview.left + dragPreview.width / 2,
                top: 0,
                transform: 'translate(-50%, -100%)',
                background: '#0b1a33',
                color: '#fff',
                padding: '3px 8px',
                borderRadius: 4,
                fontSize: 11,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 25,
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.35)',
              }}
            >
              {dragPreview.sinceLabel} - {dragPreview.tillLabel}
            </div>
          </>
        )}
      </div>
      {rightGutterWidth > 0 && (
        <div
          style={{
            width: rightGutterWidth,
            flexShrink: 0,
            minHeight: 56,
            background: gutterBg,
            transition: 'background-color 0.2s ease',
          }}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default ChannelRow;


